import React, { useState } from 'react';
import { Grid, Paper, Typography, Box, Stack, Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts';
import { feedbackHeroTokens } from './feedbackHeroTokens';

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

const SentimentRow = ({ label, value, total, tone, description, isActive, onHover, onLeave }) => {
  const { fonts, colors } = feedbackHeroTokens;
  const t = colors[tone];
  const pct = total > 0 ? (value / total) * 100 : 0;
  const pctRounded = Math.round(pct);

  return (
    <Tooltip
      title={
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{label} · {description}</Typography>
          <Typography sx={{ fontSize: '0.75rem', opacity: 0.85 }}>
            {value} {value === 1 ? 'avaliação' : 'avaliações'} ({pctRounded}% do total)
          </Typography>
        </Box>
      }
      arrow
      placement="top"
    >
      <Box
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        sx={{
          width: '100%',
          cursor: 'pointer',
          p: 0.75,
          mx: -0.75,
          borderRadius: 1.5,
          transition: 'background .2s ease, transform .2s ease',
          background: isActive ? `${t.bg}88` : 'transparent',
          transform: isActive ? 'translateX(2px)' : 'translateX(0)',
          '&:hover': {
            background: `${t.bg}88`,
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: t.accent,
                boxShadow: isActive ? `0 0 0 3px ${t.accent}33` : 'none',
                transition: 'box-shadow .2s ease',
              }}
            />
            <Typography
              sx={{
                fontFamily: fonts.body,
                fontWeight: 700,
                fontSize: '0.9rem',
                color: t.fg,
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: fonts.body, color: colors.subtext, fontSize: '0.75rem' }}
            >
              {description}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Typography
              sx={{
                fontFamily: fonts.display,
                fontWeight: 800,
                fontSize: '1.05rem',
                color: colors.text,
              }}
            >
              {value}
            </Typography>
            <Typography
              sx={{
                fontFamily: fonts.body,
                fontWeight: 600,
                fontSize: '0.78rem',
                color: colors.subtext,
              }}
            >
              ({pctRounded}%)
            </Typography>
          </Stack>
        </Stack>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 10,
            borderRadius: 6,
            background: colors.surfaceAlt,
            border: `1px solid ${colors.softBorder}`,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: `${pct}%`,
              background: `linear-gradient(90deg, ${t.accent} 0%, ${t.accent}CC 100%)`,
              transition: 'width 0.7s cubic-bezier(.2,.8,.2,1), filter .2s ease',
              filter: isActive ? 'brightness(1.05) saturate(1.15)' : 'none',
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
};

const FeedbackSentimentDistribution = ({ resumo, half = false }) => {
  const { fonts, colors } = feedbackHeroTokens;
  const [activeIndex, setActiveIndex] = useState(null);

  const getTotalBySentimento = (keys) =>
    resumo.por_sentimento?.find((s) => keys.includes(s.sentimento))?.total || 0;

  const positivos = getTotalBySentimento(['positivo', 'positivos']);
  const neutros = getTotalBySentimento(['neutro', 'neutros', 'neutral']);
  const negativos = getTotalBySentimento(['negativo', 'negativos']);
  const total = positivos + neutros + negativos;
  const pctPositivos = total > 0 ? Math.round((positivos / total) * 100) : 0;

  const data = [
    { name: 'Positivos', value: positivos, fill: colors.pos.accent, key: 'pos' },
    { name: 'Neutros', value: neutros, fill: colors.neu.accent, key: 'neu' },
    { name: 'Negativos', value: negativos, fill: colors.neg.accent, key: 'neg' },
  ];

  const hasData = total > 0;

  const centerValue = activeIndex !== null && data[activeIndex]
    ? Math.round((data[activeIndex].value / total) * 100)
    : pctPositivos;
  const centerLabel = activeIndex !== null && data[activeIndex]
    ? data[activeIndex].name
    : 'Positivos';
  const centerColor = activeIndex !== null && data[activeIndex]
    ? colors[data[activeIndex].key].fg
    : colors.pos.fg;

  const handleSegmentEnter = (_, idx) => setActiveIndex(idx);
  const handleSegmentLeave = () => setActiveIndex(null);
  const handleRowEnter = (idx) => setActiveIndex(idx);
  const handleRowLeave = () => setActiveIndex(null);

  return (
    <Grid item xs={12} md={half ? 6 : 12}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ height: '100%' }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: { xs: 2, sm: 3 },
            background: colors.surface,
            border: `1px solid ${colors.softBorder}`,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" mb={0.5}>
            <SentimentSatisfiedAltIcon sx={{ fontSize: 20, color: colors.pos.accent }} />
            <Typography
              sx={{
                fontFamily: fonts.display,
                fontWeight: 700,
                color: colors.text,
                fontSize: { xs: '1.05rem', sm: '1.15rem' },
              }}
            >
              Distribuição de sentimento
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            sx={{
              fontFamily: fonts.body,
              color: colors.subtext,
              display: 'block',
              mb: { xs: 2, sm: 2.5 },
              fontSize: '0.82rem',
            }}
          >
            Proporção entre avaliações positivas, neutras e negativas no período.
          </Typography>

          <Stack
            direction={half ? 'column' : { xs: 'column', md: 'row' }}
            spacing={half ? 2.5 : { xs: 3, md: 4 }}
            alignItems="center"
            sx={{ flex: 1, justifyContent: half ? 'center' : 'flex-start' }}
          >
            <Box
              sx={{
                position: 'relative',
                width: half ? { xs: 180, sm: 190 } : { xs: 200, sm: 220 },
                height: half ? { xs: 180, sm: 190 } : { xs: 200, sm: 220 },
                flex: '0 0 auto',
              }}
            >
              {hasData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius="70%"
                      outerRadius="100%"
                      paddingAngle={2}
                      stroke="none"
                      activeIndex={activeIndex ?? -1}
                      activeShape={renderActiveShape}
                      onMouseEnter={handleSegmentEnter}
                      onMouseLeave={handleSegmentLeave}
                      isAnimationActive={false}
                    >
                      {data.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={entry.fill}
                          style={{ cursor: 'pointer', outline: 'none' }}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: `14px solid ${colors.surfaceAlt}`,
                  }}
                />
              )}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  textAlign: 'center',
                  px: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: fonts.display,
                    fontWeight: 800,
                    color: centerColor,
                    fontSize: { xs: '2rem', sm: '2.4rem' },
                    lineHeight: 1,
                    transition: 'color .2s ease',
                  }}
                >
                  {centerValue}%
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: fonts.body,
                    color: colors.subtext,
                    fontWeight: 700,
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                    mt: 0.5,
                    fontSize: '0.7rem',
                  }}
                >
                  {centerLabel}
                </Typography>
              </Box>
            </Box>

            <Stack spacing={1.5} sx={{ flex: 1, width: '100%' }}>
              <SentimentRow
                label="Positivos"
                description="notas 4 e 5"
                value={positivos}
                total={total}
                tone="pos"
                isActive={activeIndex === 0}
                onHover={() => handleRowEnter(0)}
                onLeave={handleRowLeave}
              />
              <SentimentRow
                label="Neutros"
                description="nota 3"
                value={neutros}
                total={total}
                tone="neu"
                isActive={activeIndex === 1}
                onHover={() => handleRowEnter(1)}
                onLeave={handleRowLeave}
              />
              <SentimentRow
                label="Negativos"
                description="notas 1 e 2"
                value={negativos}
                total={total}
                tone="neg"
                isActive={activeIndex === 2}
                onHover={() => handleRowEnter(2)}
                onLeave={handleRowLeave}
              />
            </Stack>
          </Stack>
        </Paper>
      </motion.div>
    </Grid>
  );
};

export default FeedbackSentimentDistribution;
